import { useEffect, useState, useRef } from "hono/jsx";
import { ChessRoomState, WSMessage, ClientMessage, Variant as _Variant } from "../server/types.ts";
import { ChessBoard } from "./ChessBoard.tsx";
import { ChessViewNotFound } from "./views/ChessViewNotFound.tsx";

export const ChessRoom = (
  { roomId, user }: { 
    roomId: string;
    user?: {
        id: string;
        name?: string;
        isAnonymous?: boolean;
    };
  }
) => {
  const [state, setState] = useState<ChessRoomState | null>(null);
  const [yourId, setYourId] = useState<string | null>(null);
  const [_error, _setError] = useState<string | null>(null);
  const [roomNotFound, setRoomNotFound] = useState<boolean | { found: false; isReserved?: boolean; isOwner?: boolean }>(false);
  const [isCreating, setIsCreating] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [orientation, setOrientation] = useState<"white" | "black" | "red" | "blue">("white");
  const socketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    const protocol = globalThis.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${protocol}//${globalThis.location.host}/ws/chess?room=${roomId}&playerId=${user?.id || ""}`;
    const socket = new WebSocket(url);
    socketRef.current = socket;

    socket.onmessage = (event) => {
      try {
        const msg: WSMessage = JSON.parse(event.data);
        switch (msg.type) {
          case "ROOM_STATE":
            setState(msg.state);
            setYourId(msg.yourId);
            setRoomNotFound(false);
            break;
          case "ROOM_NOT_FOUND":
            setRoomNotFound({
                found: false,
                isReserved: msg.isReserved,
                isOwner: msg.isOwner,
            });
            break;
          case "ERROR":
            _setError(msg.message);
            break;
        }
      } catch (e) {
        console.error("Failed to parse message", e);
      }
    };

    return () => socket.close();
  }, [roomId, user?.id]);

  useEffect(() => {
    if (state && yourId) {
        const me = state.players.find(p => p.id === yourId);
        if (me?.color) setOrientation(me.color);
    }
  }, [state?.players.length, yourId]);

  const send = (msg: ClientMessage) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(msg));
    }
  };

  const createRoom = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const res = await fetch(`/api/chess/rooms`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customId: roomId,
          isPublic: true,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        _setError(data.error || "Failed to create room");
      }
    } catch (err) {
      console.error("Failed to create room", err);
      _setError("Network error");
    } finally {
        setIsCreating(false);
    }
  };

  if (roomNotFound) {
    return (
        <ChessViewNotFound 
            roomId={roomId} 
            info={roomNotFound as { found: false; isReserved?: boolean; isOwner?: boolean }} 
            canCreate={!!user && !user.isAnonymous}
            onCreate={createRoom}
        />
    );
  }

  if (!state) return <div class="flex items-center justify-center min-h-[50vh]"><span class="loading loading-spinner loading-lg text-primary"></span></div>;

  const isOwner = state.ownerId === yourId;
  const me = state.players.find(p => p.id === yourId);

  return (
    <div class="relative min-h-screen">
      <a 
        href="/chess" 
        class="absolute left-4 top-4 btn btn-ghost btn-sm font-black opacity-30 hover:opacity-100 uppercase"
      >
        ← Lobby
      </a>
      <a 
        href="/" 
        class="absolute left-4 top-12 btn btn-ghost btn-xs font-black opacity-20 hover:opacity-100 uppercase"
      >
        Home
      </a>
      <div class="container mx-auto p-4 flex flex-col gap-8">
        {_error && (
            <div class="alert alert-error font-black uppercase tracking-tighter shadow-lg flex justify-between items-center">
                <div class="flex items-center gap-2">
                    <span>⚠️</span>
                    <span>{_error}</span>
                </div>
                <button type="button" class="btn btn-ghost btn-xs" onClick={() => _setError(null)}>✕</button>
            </div>
        )}
        <div class="flex flex-col md:flex-row justify-between items-start gap-8">
          {/* Game Info & Board */}
          <div class="grow flex flex-col gap-4 items-center relative min-h-[600px]">
            <div class="flex items-center gap-4">
              <h1 class="text-4xl font-black text-primary uppercase italic tracking-tighter">
                  Chess <span class="text-base-content/30">#{roomId}</span>
              </h1>
              <div class={`badge badge-lg font-bold ${state.state === "PLAYING" ? "badge-success" : "badge-ghost"}`}>
                  {state.state}
              </div>
            </div>

            <div class="bg-base-300 p-8 rounded-3xl shadow-2xl border-4 border-base-100 flex items-center justify-center w-full max-w-[664px]">
             <ChessBoard 
                fen={state.fen} 
                variant={state.variant} 
                _turn={state.turn} 
                orientation={orientation}
                onMove={(move) => send({ type: "MOVE", move })}
             />
          </div>

          <div class="flex gap-2">
            <button 
                type="button" 
                class="btn btn-xs btn-outline opacity-50 hover:opacity-100"
                onClick={() => {
                    const order: ("white" | "black" | "red" | "blue")[] = ["white", "red", "black", "blue"];
                    const idx = order.indexOf(orientation);
                    setOrientation(order[(idx + 1) % 4]);
                }}
            >
                ROTATE ↻
            </button>
          </div>
          
          {state.state === "PLAYING" && (
              <div class="text-2xl font-black uppercase flex items-center gap-2">
                  <span class={`w-4 h-4 rounded-full bg-${state.turn === "white" ? "base-100 shadow-inner" : state.turn}`}></span>
                  {state.turn}'s Turn
              </div>
            )}

          {/* Lobby Overlay - now a centered card */}
          {state.state === "LOBBY" && (
            <div class="absolute inset-0 z-10 flex flex-col items-center justify-center p-4 pointer-events-none">
              <div class="bg-base-100/95 p-8 rounded-3xl shadow-2xl border-2 border-primary/20 flex flex-col items-center gap-6 max-w-sm w-full pointer-events-auto">
                <div class="text-center">
                    <h2 class="text-3xl font-black text-primary uppercase tracking-tighter mb-1">
                    {state.variant.replace("_", " ")}
                    </h2>
                    <p class="text-xs opacity-50 font-bold uppercase tracking-widest">Waiting for Players</p>
                </div>

                {isOwner
                  ? (
                    <button
                      type="button"
                      class="btn btn-primary btn-lg btn-block shadow-lg font-black uppercase text-xl h-20"
                      onClick={() => send({ type: "START_GAME" })}
                      disabled={state.players.length < (state.variant === "FOUR_PLAYER" ? 4 : 2)}
                    >
                      Start Game
                    </button>
                  )
                  : (
                    <div class="inline-block px-8 py-4 bg-primary/10 rounded-2xl border border-primary/20 animate-pulse text-center w-full">
                      <span class="font-black text-primary uppercase text-sm">
                        Waiting for Host
                      </span>
                    </div>
                  )}

                <div class="flex flex-col items-center gap-4 w-full bg-base-200 p-4 rounded-2xl border border-base-300">
                    <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(globalThis.location.href)}`}
                        alt="Room QR Code"
                        class="w-32 h-32 object-contain"
                    />
                    <div class="flex flex-col items-center gap-2 w-full">
                        <div class="text-[10px] font-mono font-bold opacity-40 uppercase">Invite Code: {state.roomId}</div>
                        <button 
                            type="button"
                            class={`btn btn-sm btn-block ${copyFeedback ? 'btn-success' : 'btn-outline'}`}
                            onClick={() => {
                                navigator.clipboard.writeText(globalThis.location.href);
                                setCopyFeedback(true);
                                setTimeout(() => setCopyFeedback(false), 2000);
                            }}
                        >
                            {copyFeedback ? 'COPIED!' : 'COPY LINK'}
                        </button>
                    </div>
                </div>
              </div>
            </div>
          )}
          </div>


          {/* Sidebar */}
          <div class="w-full md:w-80 flex flex-col gap-4">
            <div class="card bg-base-200 border border-base-300">
              <div class="card-body p-4">
                <h2 class="card-title text-sm uppercase font-black opacity-50">Players</h2>
                <div class="flex flex-col gap-2">
                  {state.players.map(p => (
                    <div key={p.id} class="flex items-center justify-between bg-base-100 p-2 rounded-xl border border-base-300">
                      <div class="flex items-center gap-2">
                        <div class={`w-3 h-3 rounded-full bg-${p.color === "white" ? "base-100 shadow-inner border border-base-300" : p.color}`}></div>
                        <span class={`font-bold ${p.id === yourId ? "text-primary" : ""}`}>{p.name}</span>
                        {p.isHost && <span class="badge badge-xs badge-primary font-black">HOST</span>}
                      </div>
                      {p.isReady && <span class="text-success text-xs font-black uppercase">READY</span>}
                    </div>
                  ))}
                </div>
                
                {state.state === "LOBBY" && (
                  <button 
                    type="button"
                    class={`btn btn-block mt-4 ${me?.isReady ? "btn-success" : "btn-neutral"}`}
                    onClick={() => send({ type: "READY" })}
                  >
                    {me?.isReady ? "UNREADY" : "READY TO PLAY"}
                  </button>
                )}
              </div>
            </div>

            {isOwner && (
              <div class="card bg-base-200 border border-primary/20">
                <div class="card-body p-4">
                  <h2 class="card-title text-sm uppercase font-black opacity-50">Settings</h2>
                  
                  <div class="form-control">
                    <label class="label cursor-pointer">
                      <span class="label-text font-bold">4-Player Mode</span>
                      <input 
                        type="checkbox" 
                        class="toggle toggle-primary" 
                        checked={state.variant === "FOUR_PLAYER"}
                        onChange={(e) => send({ type: "UPDATE_SETTINGS", settings: {}, variant: (e.target as HTMLInputElement).checked ? "FOUR_PLAYER" : "STANDARD" })}
                        disabled={state.state !== "LOBBY"}
                      />
                    </label>
                  </div>

                  <button 
                    type="button"
                    class="btn btn-primary btn-block mt-4 font-black"
                    onClick={() => send({ type: "START_GAME" })}
                    disabled={state.state === "PLAYING" || state.players.length < (state.variant === "FOUR_PLAYER" ? 4 : 2)}
                  >
                    START GAME
                  </button>
                  
                  {state.state !== "LOBBY" && (
                      <button 
                        type="button"
                        class="btn btn-error btn-outline btn-block mt-2 font-black"
                        onClick={() => send({ type: "RESET_GAME" })}
                      >
                        RESET
                      </button>
                  )}
                </div>
              </div>
            )}
            
            <div class="card bg-base-200 border border-base-300 grow h-64">
               <div class="card-body p-4 flex flex-col">
                  <h2 class="card-title text-sm uppercase font-black opacity-50">History</h2>
                  <div class="grow overflow-y-auto font-mono text-xs bg-base-100 p-2 rounded border border-base-300">
                      <div class="grid grid-cols-2 gap-x-4">
                          {state.history.map((m, i) => (
                              <div key={i} class="p-1 border-b border-base-200">
                                  <span class="opacity-30 mr-2">{Math.floor(i/2) + 1}{i%2 ? '...' : '.'}</span>
                                  <span class="font-bold">{m}</span>
                              </div>
                          ))}
                      </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
